import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { priceConcernAdminService } from '../../services'
import { getApiErrorMessage } from '../../services/apiClient'
import {
  formatDateTime,
  formatGapPct,
  formatMoney,
  isImageProof,
  isPdfProof,
  statusLabel,
  statusLogActorDisplay,
  submitterDisplay,
} from './priceConcernUtils'

export function PriceConcernDetailDrawer({
  open,
  concern,
  statusLogRefreshKey = 0,
  onClose,
}) {
  const theme = useTheme()
  const fullWidth = useMediaQuery(theme.breakpoints.down('sm'))
  const [statusLogs, setStatusLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsError, setLogsError] = useState(null)

  useEffect(() => {
    if (!open || !concern?.id) {
      setStatusLogs([])
      setLogsError(null)
      return undefined
    }

    let cancelled = false
    setLogsLoading(true)
    setLogsError(null)

    priceConcernAdminService
      .getStatusLogs(concern.id)
      .then((data) => {
        if (!cancelled) {
          setStatusLogs(Array.isArray(data) ? data : [])
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setStatusLogs([])
          setLogsError(getApiErrorMessage(err, 'Failed to load status history'))
        }
      })
      .finally(() => {
        if (!cancelled) setLogsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, concern?.id, statusLogRefreshKey])

  if (!concern) return null

  const proof = concern.proof
  const proofUrl = proof?.url || null

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: fullWidth ? '100%' : 440,
          maxWidth: '100%',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700}>
            Concern details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            #{concern.id} · Submitted {formatDateTime(concern.submitted_at)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto' }}>
        <Box
          sx={{
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            mb: 2.5,
          }}
        >
          <Typography fontWeight={700}>{concern.product_name || '—'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            Article #{concern.article_code || '—'}
          </Typography>
        </Box>

        <SectionLabel>Price comparison</SectionLabel>
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
          <CompareCard label="Shwapno price" value={formatMoney(concern.shwapno_price)} />
          <CompareCard label="Reported price" value={formatMoney(concern.dealer_price)} />
        </Stack>
        <Box
          sx={{
            display: 'inline-flex',
            px: 1.25,
            py: 0.5,
            borderRadius: 1,
            bgcolor: '#FDF1E1',
            color: '#A9600C',
            fontWeight: 800,
            fontSize: 13,
            mb: 2.5,
          }}
        >
          Gap {formatGapPct(concern.gap_pct)}
        </Box>

        <SectionLabel>Submission details</SectionLabel>
        <InfoRow
          label="Shop"
          value={`${concern.shop_name || '—'} (Shop ID ${concern.org_id})`}
        />
        <InfoRow label="Submitted by" value={submitterDisplay(concern.submitted_by)} />
        <InfoRow label="Submitted on" value={formatDateTime(concern.submitted_at)} />
        <InfoRow label="Status" value={statusLabel(concern.status)} />
        <Box sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Grocer&apos;s note
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {concern.grocer_note?.trim() ? concern.grocer_note.trim() : '—'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <SectionLabel>Proof</SectionLabel>
        {!proof ? (
          <Typography color="text.secondary">No attachment</Typography>
        ) : (
          <Box>
            {isImageProof(proof) && proofUrl ? (
              <Box
                component="img"
                src={proofUrl}
                alt={proof.name || 'Proof'}
                sx={{
                  width: '100%',
                  maxHeight: 240,
                  objectFit: 'contain',
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 1.5,
                  bgcolor: 'background.default',
                }}
              />
            ) : null}
            <Typography variant="body2" sx={{ mb: 1 }}>
              {proof.name || 'Attachment'}
            </Typography>
            <Stack direction="row" spacing={1}>
              {proofUrl ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  component={Link}
                  href={proofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {isPdfProof(proof) ? 'Open PDF' : 'Open'}
                </Button>
              ) : null}
              {proofUrl ? (
                <Button
                  size="small"
                  variant="text"
                  component={Link}
                  href={proofUrl}
                  download={proof.name || true}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download
                </Button>
              ) : null}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <SectionLabel>Trace log</SectionLabel>
        {logsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : logsError ? (
          <Typography variant="body2" color="error">
            {logsError}
          </Typography>
        ) : statusLogs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No admin status changes recorded yet.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {statusLogs.map((entry) => (
              <Box
                key={entry.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 1.25,
                  bgcolor: 'background.default',
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {statusLabel(entry.from_status)} → {statusLabel(entry.to_status)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  By {statusLogActorDisplay(entry)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatDateTime(entry.created_at)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'text.secondary',
        fontWeight: 700,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  )
}

function CompareCard({ label, value }) {
  return (
    <Box
      sx={{
        flex: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  )
}

function InfoRow({ label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right">
        {value}
      </Typography>
    </Box>
  )
}
